import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { Activity, Shield, Briefcase } from 'lucide-react';
import type { components } from '@aaimplatform/institutional-types/pe-fund-analytics';
import type { components as valuationComponents } from '@aaimplatform/institutional-types/pe-valuation';
import {
  createPeFundClient,
  createPeValuationClient,
} from '@aaimplatform/institutional-types/api-clients';
import { Alert, Button, EmptyState, MetricCard, MetricCardGrid, METRIC_READ_CAPTIONS, TableScroll } from '@aaimplatform/ui';
import { csrfMiddleware } from '../../api/client';
import { mapPeFetchError } from './pe-error';
import { FUND_STRATEGY_LABELS, RISK_RATING_LABELS, labelFor } from './pe-labels';
import { ListHeader } from '../collateral/CollateralListParts';
import {
  formatCurrencyTotalsCompact,
  formatDate,
  formatMoney,
  formatPercent,
  totalsByCurrency,
  NOT_RECORDED,
} from '../lien/format';

// ---------------------------------------------------------------------------
// API Configuration
// ---------------------------------------------------------------------------

const peFundClient = createPeFundClient();
const peValuationClient = createPeValuationClient();

// CSRF double-submit echo (N8) on mutating PE-valuation requests. pe-valuation
// mounts `enforceCsrf: true` (GH #2653), so a cookie-authenticated POST without
// the X-CSRF-Token header 403s. Mirrors advisor-tools PeLpValuationPage.tsx:30.
// peFundClient is read-only on this page; safe methods carry no header anyway,
// and it is registered too so a future write on it is covered.
peFundClient.use(csrfMiddleware);
peValuationClient.use(csrfMiddleware);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LpInterest = components['schemas']['LpInterest'];
type PeFund = components['schemas']['PeFund'];
type CollateralAssessmentRequest = valuationComponents['schemas']['CollateralAssessmentRequest'];
type CollateralAssessmentResponse = valuationComponents['schemas']['CollateralAssessmentResponse'];
type StressScenarioResult = valuationComponents['schemas']['StressScenarioResult'];

// ---------------------------------------------------------------------------
// Assessment request, built only from recorded values
// ---------------------------------------------------------------------------

/**
 * The strategies LpInterestValuationRequest accepts. A fund whose strategy is
 * outside this set (credit, secondary, real assets ...) is sent without one, and
 * the engine applies its own default rather than a strategy the page invented.
 */
const VALUATION_STRATEGY: Record<
  NonNullable<CollateralAssessmentRequest['interestValuation']['fundStrategy']>,
  true
> = {
  large_cap_buyout: true,
  mid_market_buyout: true,
  small_cap_buyout: true,
  growth_equity: true,
  venture_capital: true,
  permanent_capital: true,
};

function valuationStrategy(
  strategy: string,
): CollateralAssessmentRequest['interestValuation']['fundStrategy'] {
  return strategy in VALUATION_STRATEGY
    ? (strategy as keyof typeof VALUATION_STRATEGY)
    : undefined;
}

/**
 * The `default` values FundTermsForAssessment declares in
 * pe-valuation.openapi.yaml. The generated type marks a defaulted property
 * required, so a term the fund record does not carry is sent as its declared
 * default, and the page lists it as assumed rather than presenting it as the
 * fund's recorded term.
 */
const FUND_TERM_CONTRACT_DEFAULTS = {
  gpConsentRequired: true,
  gpConsentTimelineDays: 30,
  rofrApplies: true,
  rofrExercisePeriodDays: 30,
  pledgePermitted: true,
};

const FUND_TERM_LABELS: Record<keyof typeof FUND_TERM_CONTRACT_DEFAULTS, string> = {
  gpConsentRequired: 'GP consent requirement',
  gpConsentTimelineDays: 'GP consent timeline',
  rofrApplies: 'right of first refusal',
  rofrExercisePeriodDays: 'ROFR exercise period',
  pledgePermitted: 'pledge permission',
};

/**
 * Build the collateral-assessment request for one pledged LP interest.
 *
 * Fund terms come from the fund record's `transferRestrictions`, `waterfallType`,
 * `fundTermYears` and `vintageYear`. A term the record does not carry takes the
 * contract's declared default and is returned in `assumedTerms`, which the page
 * shows beside the assessment.
 *
 * The fund-level NAV is derived as the interest's NAV divided by its ownership
 * share, so an interest with no recorded ownership percentage cannot be assessed.
 * It is refused with the reason, never defaulted. The share is a fraction of the
 * fund, greater than 0 and at most 1; a stored value above 1 is a 0-100 entry on
 * that scale and is treated as not recorded, as the engine treats it.
 */
export function buildAssessmentRequest(
  lp: LpInterest,
  fund: PeFund,
  asOf: Date,
):
  | { ok: true; body: CollateralAssessmentRequest; assumedTerms: string[] }
  | { ok: false; reason: string } {
  if (lp.interestPercent == null || !(lp.interestPercent > 0) || lp.interestPercent > 1) {
    return {
      ok: false,
      reason:
        'Ownership percentage is not recorded for this LP interest, so the fund NAV cannot be derived.',
    };
  }
  if (!(lp.commitmentAmount > 0)) {
    return {
      ok: false,
      reason: 'Commitment amount is not recorded, so the called share of the commitment cannot be derived.',
    };
  }

  const recorded: Partial<typeof FUND_TERM_CONTRACT_DEFAULTS> = {};
  const restrictions = fund.transferRestrictions;
  if (restrictions?.gpConsentRequired != null) recorded.gpConsentRequired = restrictions.gpConsentRequired;
  if (restrictions?.rofrApplies != null) recorded.rofrApplies = restrictions.rofrApplies;
  if (restrictions?.rofrExercisePeriodDays != null) {
    recorded.rofrExercisePeriodDays = restrictions.rofrExercisePeriodDays;
  }
  if (restrictions?.pledgePermitted != null) recorded.pledgePermitted = restrictions.pledgePermitted;

  const terms = { ...FUND_TERM_CONTRACT_DEFAULTS };
  const assumedTerms: string[] = [];
  for (const key of Object.keys(FUND_TERM_CONTRACT_DEFAULTS) as Array<keyof typeof FUND_TERM_CONTRACT_DEFAULTS>) {
    if (key in recorded) {
      (terms as Record<string, boolean | number>)[key] = recorded[key] as boolean | number;
    } else {
      assumedTerms.push(FUND_TERM_LABELS[key]);
    }
  }

  const fundTerms: CollateralAssessmentRequest['fundTerms'] = {
    ...terms,
    fundTermRemainingYears: Math.max(0, fund.fundTermYears - (asOf.getUTCFullYear() - fund.vintageYear)),
  };
  if (fund.waterfallType) fundTerms.waterfallType = fund.waterfallType;

  return {
    ok: true,
    body: {
      interestId: lp.id,
      interestValuation: {
        fundId: lp.fundId,
        interestId: lp.id,
        commitmentAmount: lp.commitmentAmount,
        calledCapitalPercent: lp.calledAmount / lp.commitmentAmount,
        distributionsToDate: lp.distributedAmount,
        // interestPercent is the LP's fraction of the fund (0-1), so NAV / fraction is the fund NAV.
        currentFundNav: lp.currentNav / lp.interestPercent,
        interestPercent: lp.interestPercent,
        unfundedCommitment: lp.unfundedCommitment,
        fundVintageYear: fund.vintageYear,
        fundStrategy: valuationStrategy(fund.strategy),
        includeUnfundedLiability: true,
      },
      fundTerms,
    },
    assumedTerms,
  };
}

/**
 * Fixed stress scenarios. They are the same three the engine's collateral
 * report uses; they are not read from the institution's collateral policy, and
 * the stress panel says so.
 */
export const FIXED_STRESS_SCENARIOS = [
  { name: 'Moderate downturn', navDeclinePercent: 0.25 },
  { name: 'Severe recession', navDeclinePercent: 0.45 },
  { name: 'Extreme stress', navDeclinePercent: 0.6 },
];

function getRiskRatingStyle(rating: string): string {
  switch (rating) {
    case 'low':
      return 'bg-growth/10 text-growth';
    case 'moderate':
      return 'bg-warning/10 text-warning';
    case 'elevated':
    case 'high':
    case 'very_high':
      return 'bg-error/10 text-error';
    default:
      return 'bg-quaternary/10 text-quaternary';
  }
}

const TH = 'px-4 py-3 text-left text-label-xs font-sans font-semibold uppercase text-tertiary leading-normal';

// ---------------------------------------------------------------------------
// PE Collateral Monitoring Page
// ---------------------------------------------------------------------------

/**
 * PeCollateralMonitoringPage
 *
 * Lists the pledged LP interest positions across this institution's PE funds,
 * and runs a collateral assessment or a fixed-scenario stress test per position.
 *
 * Fetches all funds, then for each fund fetches LP interests filtered to
 * isPledged=true. A fund whose LP interests could not be read is named, and the
 * totals say they exclude it.
 */
export function PeCollateralMonitoringPage() {
  const [positions, setPositions] = useState<Array<{
    lp: LpInterest;
    fund: PeFund;
    assessment: CollateralAssessmentResponse | null;
    isAssessing: boolean;
    stressResults: StressScenarioResult[] | null;
    isStressTesting: boolean;
  }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [unreadFunds, setUnreadFunds] = useState<string[]>([]);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  // One date for every request this mount builds, so a remaining-term figure
  // does not change between two assessments run a minute apart.
  const asOf = useMemo(() => new Date(), []);

  // -------------------------------------------------------------------------
  // Data Fetching
  // -------------------------------------------------------------------------

  const fetchPledgedPositions = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const { data: fundsResult, error: fundsError } = await peFundClient.GET('/api/v1/pe/funds');
      if (fundsError || !fundsResult) throw new Error('Failed to load funds');
      const funds = fundsResult.data ?? [];

      const allPositions: typeof positions = [];
      const failed: string[] = [];

      await Promise.all(
        funds.map(async (fund) => {
          try {
            const { data: lpResult, error: lpError } = await peFundClient.GET(
              '/api/v1/pe/funds/{fundId}/lp-interests',
              { params: { path: { fundId: fund.id } } }
            );
            if (lpError || !lpResult) {
              failed.push(fund.name);
              return;
            }
            for (const lp of (lpResult.data ?? []).filter((l) => l.isPledged)) {
              allPositions.push({
                lp,
                fund,
                assessment: null,
                isAssessing: false,
                stressResults: null,
                isStressTesting: false,
              });
            }
          } catch {
            failed.push(fund.name);
          }
        })
      );

      setPositions(allPositions);
      setUnreadFunds(failed.sort());
    } catch (err) {
      setPositions([]);
      setUnreadFunds([]);
      setLoadError(mapPeFetchError(err, 'load pledged positions'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPledgedPositions();
  }, [fetchPledgedPositions]);

  // -------------------------------------------------------------------------
  // Assessment Handler
  // -------------------------------------------------------------------------

  const runAssessment = useCallback(
    async (lpInterestId: string) => {
      const pos = positions.find((p) => p.lp.id === lpInterestId);
      if (!pos) return;
      const request = buildAssessmentRequest(pos.lp, pos.fund, asOf);
      if (!request.ok) return; // the Assess button is disabled and the reason is shown

      setPositions((prev) =>
        prev.map((p) => (p.lp.id === lpInterestId ? { ...p, isAssessing: true } : p))
      );
      try {
        const { data, error: fetchError } = await peValuationClient.POST(
          '/api/v1/pe/valuation/collateral-assessment',
          { body: request.body }
        );
        if (fetchError || !data) throw new Error('Failed to run collateral assessment');
        setPositions((prev) =>
          prev.map((p) =>
            p.lp.id === lpInterestId ? { ...p, assessment: data, isAssessing: false } : p
          )
        );
      } catch (err) {
        setActionError(mapPeFetchError(err, 'run collateral assessment'));
        setPositions((prev) =>
          prev.map((p) => (p.lp.id === lpInterestId ? { ...p, isAssessing: false } : p))
        );
      }
    },
    [positions, asOf]
  );

  // -------------------------------------------------------------------------
  // Stress Test Handler
  // -------------------------------------------------------------------------

  const runStressTest = useCallback(async (lpInterestId: string) => {
    setPositions((prev) =>
      prev.map((p) => (p.lp.id === lpInterestId ? { ...p, isStressTesting: true } : p))
    );
    try {
      const { data, error: fetchError } = await peValuationClient.POST(
        '/api/v1/pe/valuation/stress-test',
        { body: { interestId: lpInterestId, scenarios: FIXED_STRESS_SCENARIOS } }
      );
      if (fetchError || !data) throw new Error('Failed to run stress test');
      setPositions((prev) =>
        prev.map((p) =>
          p.lp.id === lpInterestId
            ? { ...p, stressResults: data.scenarioResults, isStressTesting: false }
            : p
        )
      );
    } catch (err) {
      setActionError(mapPeFetchError(err, 'run stress test'));
      setPositions((prev) =>
        prev.map((p) => (p.lp.id === lpInterestId ? { ...p, isStressTesting: false } : p))
      );
    }
  }, []);

  /** Expand runs the test once; collapse only collapses. */
  const toggleStress = (pos: (typeof positions)[number]) => {
    if (expandedRow === pos.lp.id) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(pos.lp.id);
    if (!pos.stressResults && !pos.isStressTesting) {
      void runStressTest(pos.lp.id);
    }
  };

  // -------------------------------------------------------------------------
  // Summary Metrics
  // -------------------------------------------------------------------------

  const navTotals = useMemo(
    () => totalsByCurrency(positions, (p) => p.lp.currentNav, (p) => p.fund.currency),
    [positions]
  );
  const commitmentTotals = useMemo(
    () => totalsByCurrency(positions, (p) => p.lp.commitmentAmount, (p) => p.fund.currency),
    [positions]
  );

  const cardsRead = !isLoading && !loadError;
  const cardCaption = isLoading
    ? METRIC_READ_CAPTIONS.unread
    : loadError
      ? METRIC_READ_CAPTIONS.failed
      : undefined;
  const partialCaption =
    unreadFunds.length > 0
      ? `Excludes ${unreadFunds.length} unread fund${unreadFunds.length === 1 ? '' : 's'}`
      : undefined;
  const currencyCaption = (count: number) =>
    count === 0 ? 'No pledged positions' : count > 1 ? `${count} currencies, not converted` : undefined;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div>
      {/* The shared list header: Refresh keeps its own width on a phone, as
          on every page of the group (UI run r3, fi-g5-4). */}
      <ListHeader
        onRefresh={() => void fetchPledgedPositions()}
        refreshing={isLoading}
        subtitle={
          <p className="text-sm font-sans text-tertiary">
            Private equity collateral valuation and stress testing
          </p>
        }
      />

      {loadError && (
        <Alert type="error" title="Pledged positions could not be read" className="mb-6">
          {loadError}
        </Alert>
      )}
      {actionError && (
        <Alert type="error" className="mb-6">
          <div className="flex items-start justify-between gap-4">
            <span>{actionError}</span>
            <Button variant="tertiary" size="sm" onClick={() => setActionError(null)} aria-label="Dismiss error">
              Dismiss
            </Button>
          </div>
        </Alert>
      )}
      {unreadFunds.length > 0 && (
        <Alert type="warning" title="Some funds could not be read" className="mb-6">
          LP interests for {unreadFunds.join(', ')} could not be read. The positions and
          totals below exclude them.
        </Alert>
      )}

      <MetricCardGrid columns={3} className="mb-6">
        <MetricCard
          label="Pledged positions"
          value={cardsRead ? String(positions.length) : NOT_RECORDED}
          caption={cardCaption ?? partialCaption}
        />
        <MetricCard
          label="Total current NAV"
          value={cardsRead ? formatCurrencyTotalsCompact(navTotals) : NOT_RECORDED}
          caption={cardCaption ?? partialCaption ?? currencyCaption(navTotals.length)}
        />
        <MetricCard
          label="Total commitment"
          value={cardsRead ? formatCurrencyTotalsCompact(commitmentTotals) : NOT_RECORDED}
          caption={cardCaption ?? partialCaption ?? currencyCaption(commitmentTotals.length)}
        />
      </MetricCardGrid>

      {!loadError && (
      <div className="bg-surface rounded-sm shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <p className="text-sm font-sans text-tertiary">Loading pledged positions...</p>
          </div>
        ) : positions.length === 0 ? (
          <EmptyState
            icon={<Briefcase className="h-8 w-8" />}
            title="No LP interests in your institution's funds are marked as pledged"
            description="A pledged LP interest appears here with its NAV, commitment, and the assessment and stress-test actions."
          />
        ) : (
          <TableScroll label="Pledged LP interests">
            <table className="w-full min-w-[960px]">
              <thead>
                <tr className="bg-elevated border-b border-recessed">
                  <th scope="col" className={TH}>LP name</th>
                  <th scope="col" className={TH}>Fund</th>
                  <th scope="col" className={`${TH} text-right`}>Commitment</th>
                  <th scope="col" className={`${TH} text-right`}>Current NAV</th>
                  <th scope="col" className={`${TH} text-center`}>Recommended advance rate</th>
                  <th scope="col" className={`${TH} text-center`}>Risk rating</th>
                  <th scope="col" className={`${TH} text-center`}>Last assessment</th>
                  <th scope="col" className={`${TH} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((pos) => {
                  const isExpanded = expandedRow === pos.lp.id;
                  const request = buildAssessmentRequest(pos.lp, pos.fund, asOf);
                  const reasonId = `assess-reason-${pos.lp.id}`;
                  const panelId = `stress-panel-${pos.lp.id}`;
                  const guidance = pos.assessment?.advanceRateGuidance;

                  return (
                    <Fragment key={pos.lp.id}>
                      <tr className="border-b border-recessed hover:bg-elevated transition-colors">
                        <td className="px-4 py-4">
                          <span className="text-sm font-sans font-medium text-primary leading-normal">
                            {pos.lp.lpName}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-sans text-primary leading-normal block">
                            {pos.fund.name}
                          </span>
                          <span className="text-xs font-sans text-tertiary">
                            {labelFor(FUND_STRATEGY_LABELS, pos.fund.strategy)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
                            {formatMoney(pos.lp.commitmentAmount, pos.fund.currency)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
                            {formatMoney(pos.lp.currentNav, pos.fund.currency)}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-center">
                          {pos.assessment ? (
                            <>
                              <span className="text-sm font-mono text-primary tabular-nums leading-normal">
                                {formatPercent(pos.assessment.recommendedLtv)}
                              </span>
                              {guidance?.typicalAdvanceRateLow != null &&
                                guidance.typicalAdvanceRateHigh != null && (
                                  <span className="block text-xs font-sans text-tertiary">
                                    Guidance {formatPercent(guidance.typicalAdvanceRateLow)} to{' '}
                                    {formatPercent(guidance.typicalAdvanceRateHigh)}
                                  </span>
                                )}
                            </>
                          ) : (
                            <span className="text-xs font-sans text-tertiary">{NOT_RECORDED}</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center">
                          {pos.assessment ? (
                            <span
                              className={`inline-flex px-3 py-1 text-xs font-sans font-semibold rounded-sm leading-normal ${getRiskRatingStyle(
                                pos.assessment.riskRating
                              )}`}
                            >
                              {labelFor(RISK_RATING_LABELS, pos.assessment.riskRating)}
                            </span>
                          ) : (
                            <span className="text-xs font-sans text-tertiary">{NOT_RECORDED}</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center">
                          {!request.ok ? (
                            <span id={reasonId} className="text-xs font-sans text-secondary">
                              Cannot assess. {request.reason}
                            </span>
                          ) : (
                            <>
                              {pos.assessment ? (
                                <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
                                  {formatDate(pos.assessment.calculatedAt)}
                                </span>
                              ) : (
                                <span className="text-xs font-sans text-tertiary">Not assessed</span>
                              )}
                              {request.assumedTerms.length > 0 && (
                                <span className="block text-xs font-sans text-tertiary">
                                  Not on the fund record, contract default used: {request.assumedTerms.join(', ')}
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => runAssessment(pos.lp.id)}
                              disabled={pos.isAssessing || !request.ok}
                              aria-describedby={request.ok ? undefined : reasonId}
                            >
                              <Activity className="w-3 h-3" aria-hidden="true" />
                              {pos.isAssessing ? 'Assessing...' : 'Assess'}
                            </Button>
                            <Button
                              variant="tertiary"
                              size="sm"
                              onClick={() => toggleStress(pos)}
                              aria-expanded={isExpanded}
                              aria-controls={panelId}
                            >
                              <Shield className="w-3 h-3" aria-hidden="true" />
                              {isExpanded ? 'Hide stress test' : 'Stress test'}
                            </Button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td id={panelId} colSpan={8} className="px-6 py-4 bg-elevated/50">
                            <p className="text-label-xs font-sans font-semibold uppercase text-tertiary">
                              Stress test: {pos.lp.lpName}
                            </p>
                            <p className="mt-1 mb-3 text-xs font-sans text-tertiary">
                              Fixed scenarios of 25%, 45% and 60% NAV decline. They are not
                              drawn from your institution's collateral policy.
                            </p>
                            {pos.isStressTesting ? (
                              <p className="text-sm font-sans text-tertiary">Running stress test...</p>
                            ) : !pos.stressResults ? (
                              <p className="text-sm font-sans text-tertiary">
                                The stress test did not return results.{' '}
                                <Button variant="tertiary" size="sm" onClick={() => runStressTest(pos.lp.id)}>
                                  Run again
                                </Button>
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {pos.stressResults.map((scenario) => (
                                  <div
                                    key={scenario.scenarioName}
                                    className={`p-4 rounded-sm border border-recessed ${
                                      scenario.marginCallTriggered ? 'bg-error/5' : 'bg-surface'
                                    }`}
                                  >
                                    <div className="text-xs font-sans font-semibold text-primary mb-2">
                                      {scenario.scenarioName}
                                    </div>
                                    <div className="space-y-1 text-sm font-mono tabular-nums">
                                      <div className="flex justify-between">
                                        <span className="text-tertiary">Stressed NAV</span>
                                        <span className="text-primary">
                                          {formatMoney(scenario.stressedNav, pos.fund.currency)}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-tertiary">Stressed LTV</span>
                                        <span
                                          className={
                                            scenario.marginCallTriggered ? 'text-error font-semibold' : 'text-primary'
                                          }
                                        >
                                          {formatPercent(scenario.stressedLtv)}
                                        </span>
                                      </div>
                                    </div>
                                    {scenario.marginCallTriggered && (
                                      <div className="mt-2 px-2 py-1 text-xs font-sans font-semibold text-error bg-error/10 rounded-sm text-center">
                                        CHA tier breach
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </TableScroll>
        )}
      </div>
      )}
    </div>
  );
}
